import { useState, useEffect, useCallback } from "react";
import type { Visit, AllVisits } from "@/types";
import { dbGet, dbSet } from "@/lib/db";
import { SK, todayKey } from "@/lib/constants";

export function useVisits() {
  const [visits, setVisits] = useState<AllVisits>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dbGet<AllVisits>(SK.VISITS).then((v) => {
      setVisits(v ?? {});
      setLoading(false);
    });
  }, []);

  const persist = useCallback((next: AllVisits) => {
    setVisits(next);
    dbSet(SK.VISITS, next);
  }, []);

  const addVisit = useCallback(
    (visit: Omit<Visit, "time" | "date">) => {
      const date = todayKey();
      const time = new Date().toLocaleTimeString("pt-PT", {
        hour: "2-digit",
        minute: "2-digit",
      });
      const full: Visit = { ...visit, date, time };
      const dayVisits = [...(visits[date] ?? []), full];
      persist({ ...visits, [date]: dayVisits });
      return full;
    },
    [visits, persist]
  );

  const todayVisits = visits[todayKey()] ?? [];

  const resetDay = useCallback(() => {
    const date = todayKey();
    const { [date]: _, ...rest } = visits;
    persist(rest);
  }, [visits, persist]);

  const setAllVisits = useCallback(
    (v: AllVisits) => persist(v),
    [persist]
  );

  return { visits, loading, addVisit, todayVisits, resetDay, setAllVisits };
}
